import { showNotification } from '@helpers/notification-helper';
import useOperationStatus from '@lib/hooks/useOperationStatus';
import { actions as eVisitActions } from '@store/ducks/e-visits';
import { Form, Modal, TextField } from '@ui';
import moment from 'moment';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { EVisitItem } from 'types/e-visits';
import * as Yup from 'yup';

interface CancelVisitModalProps {
  visible: boolean;
  event?: EVisitItem;
  onClose?: () => void;
  onSuccess?: () => void;
}

const VALIDATION_SCHEMA = Yup.object({
  reason: Yup.string().nullable(),
});

export const CancelVisitModal: React.FunctionComponent<CancelVisitModalProps> = ({
  visible,
  event,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const { status } = useOperationStatus({
    stateGetter: (state) => state.eVisits.cancelEVisitStatus,
    resetAction: eVisitActions.cancelEVisitReset,
    onSuccess: () => {
      onClose?.();
      onSuccess?.();
    },
    onError: () => {
      showNotification({
        type: 'ERROR',
        title: 'Niečo sa pokazilo',
        copy: '',
      });
    },
  });

  const deleteBooking = useCallback(
    (reason: string) => {
      dispatch(
        eVisitActions.cancelEVisitRequest({
          orderId: event!.order!.id,
          reason,
        }),
      );
    },
    [event],
  );

  const PatientName = useMemo(() => {
    if (!event || !event.order?.patientProfile) {
      return null;
    } else if (event.order?.patientProfile) {
      return (
        <div className="name">{`${event.order?.patientProfile.firstName} ${event.order?.patientProfile.lastName} ,`}</div>
      );
    }

    return null;
  }, [event]);

  const [form, formProps] = Form.useForm({
    validationSchema: VALIDATION_SCHEMA,
    initialValues: { reason: '' },
    onSubmit: ({ reason }) => {
      deleteBooking(reason);
    },
  });

  if (!event) {
    return null;
  }

  const from = moment(event.event.from).format('D. MMMM HH:mm');
  const to = moment(event.event.to).format('HH:mm');

  return (
    <Modal
      title={'Zrušenie termínu'}
      visible={visible}
      onClose={onClose}
      destroyOnClose
      footer={
        <Modal.Actions
          actions={[
            { key: 'cancel', label: 'Zrušiť', onClick: onClose, variant: 'ghost' },
            {
              key: 'submit',
              label: <FormattedMessage id="cancelVisitModal_submitButton" />,
              variant: 'danger',
              loading: status === 'pending',
              onClick: () => form?.submitForm?.(),
            },
          ]}
        />
      }
    >
      <Form {...formProps}>
        <Modal.Title>
          <FormattedMessage id="cancelVisitModal_title" />
        </Modal.Title>
        <Modal.Description>
          {PatientName}
          {from} - {to}
        </Modal.Description>
        <Form.Field name="reason" label={formatMessage({ id: 'cancelVisitModal_reason' })}>
          <TextField.TextArea
            placeholder={formatMessage({ id: 'cancelVisitModal_placeholder' })}
            style={{ width: '100%' }}
          />
        </Form.Field>
      </Form>
    </Modal>
  );
};

export default React.memo(CancelVisitModal);
